import { faTrash } from "@fortawesome/pro-solid-svg-icons"
import { translate } from "app/language/service"
import { ICsvFileColumn, NOT_SAVED_ID } from "app/model"
import { getStore } from "app/store/store"
import { downloadCsvFile, generateCsvFileContent, isImageFile, isVideoFile } from "app/tools/file"
import { formatDate } from "app/util/date/tools/common"
import { CANCELED_REJECTION, IForm, IFormMediaCreativeCampaignRow, IFormMediaCreativeForecastRow } from "form/model"
import { formActionCreator } from "form/store/actions"
import { IMedia, MEDIA_CONTENT_TYPES } from "media/model"
import { deleteCampaignMedia } from "media/tools/common"
import { getContentTypeId } from "media/tools/media-type"
import { messageSVC } from "message/service"
import { getForm } from "./common"
import { getCreativeChange, getFilteredCreativeRows } from "./creative"
import { getMediaCreativeForecastInfo, getMediaCreativeForecastRows } from "./creative-forecast"

export function getMediaCreativeCampaignRows(form: IForm): IFormMediaCreativeCampaignRow[] {
  const {
    creatives: { medias }
  } = form
  const forecastRows = getMediaCreativeForecastRows(form)
  const mediaByKey: { [key: string]: IMedia } = {}
  const allExistingMediaKeys: string[] = []
  const foundMediaKeys: string[] = []
  for (const media of medias) {
    if (media.creatives.length === 0) {
      continue
    }
    const mediaKey = getMediaKey(
      getContentTypeId(MEDIA_CONTENT_TYPES.VIDEO) === media.content_type_id,
      media.format_id,
      media.width,
      media.height
    )
    allExistingMediaKeys.push(mediaKey)
    mediaByKey[mediaKey] = media
  }
  const campaignRows: IFormMediaCreativeCampaignRow[] = []
  let notUploadedMediaId = -2
  for (const forecastRow of forecastRows) {
    const campaignRow: IFormMediaCreativeCampaignRow = getEmptyCampaignRow(forecastRow)
    const forecastMediaKey = getMediaKey(
      forecastRow.mediaType === "video",
      forecastRow.formatId,
      forecastRow.width,
      forecastRow.height
    )
    const media = mediaByKey[forecastMediaKey]
    if (media) {
      foundMediaKeys.push(forecastMediaKey)
      fillRowWithMediaCreative(campaignRow, media)
    } else {
      campaignRow.id = `${--notUploadedMediaId}`
    }
    campaignRows.push(campaignRow)
  }
  if (foundMediaKeys.length !== allExistingMediaKeys.length) {
    const missingMediaKeys = allExistingMediaKeys.filter(key => !foundMediaKeys.includes(key))
    for (const missingMediaKey of missingMediaKeys) {
      const media = mediaByKey[missingMediaKey]
      const info = getMediaCreativeForecastInfo(media)
      if (!info) {
        continue
      }
      const campaignRow = getEmptyCampaignRow({
        impressionRatio: "",
        impressionDiff: 0,
        screensRatio: "",
        screensDiff: 0,
        screen: 0,
        screenPercent: 0,
        screenTotal: 0,
        impression: 0,
        impressionPercent: 0,
        impressionTotal: 0,
        spendInCad: 0,
        spendTotalInCad: 0,
        isUploaded: true,
        ...info
      })
      fillRowWithMediaCreative(campaignRow, media)
      campaignRows.push(campaignRow)
    }
  }
  return campaignRows
}

export const deleteFormCreative = (campaignId: number, mediaId: number, campaignMediaId: number): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    // only delete the campaign/media association
    // the orphan media will be deleted server side in a batch
    const form = getForm()
    const media = form.creatives.medias.find(m => m.id === mediaId)
    if (!media) {
      return reject(`Media id=${mediaId} not found`)
    }
    messageSVC.confirmMessage({
      title: translate("campaign.creative.delete"),
      icon: faTrash,
      addGenericConfirmMessage: true,
      message: translate("campaign.creative.delete.confirm"),
      confirmButtonLabel: translate("button.delete"),
      onSuccess: async () => {
        try {
          await deleteCampaignMedia(campaignId, campaignMediaId)
          const newMedias = form.creatives.medias.filter(m => m.id !== mediaId)
          getStore().dispatch(
            formActionCreator.setCreatives(
              form.context.appContext,
              { medias: newMedias },
              getCreativeChange(form, newMedias)
            )
          )
          resolve()
        } catch (error) {
          console.error(error)
          reject("Unexpected error while deleting form creative")
        }
      },
      onCancel: () => reject(CANCELED_REJECTION)
    })
  })

export const downloadRequiredCreatives = (form: IForm): void => {
  const rows = getMediaCreativeCampaignRows(form)
  if (rows.length === 0) {
    messageSVC.info(translate("campaign.creative.download.empty"))
    return
  }
  const filteredRows = getFilteredCreativeRows(form.creatives.filter, rows)
  if (filteredRows.length === 0) {
    messageSVC.info(translate("campaign.creative.download.empty"))
    return
  }
  const columns: ICsvFileColumn[] = [
    {
      key: "mediaType",
      label: translate("label.type")
    },
    {
      key: "width",
      label: translate("label.width")
    },
    {
      key: "height",
      label: translate("label.height")
    },
    {
      key: "impressionPercent",
      label: `${translate("label.impression.percent")} %`
    },
    {
      key: "screenPercent",
      label: `${translate("label.screen.percent")} %`
    }
  ]
  const fileContent: string = generateCsvFileContent(columns, filteredRows)
  const fileName: string = `required-creatives-${formatDate(new Date())}`
  downloadCsvFile(fileName, fileContent)
}

function getEmptyCampaignRow(forecastRow: IFormMediaCreativeForecastRow): IFormMediaCreativeCampaignRow {
  return {
    ...forecastRow,
    id: "",
    mediaId: NOT_SAVED_ID,
    campaignMediaId: NOT_SAVED_ID,
    creativeId: NOT_SAVED_ID,
    name: "",
    createdAt: null,
    type: "" as any,
    approval_status_id: undefined,
    isUploaded: false
  }
}

function getMediaKey(isVideo: boolean, formatId: number | null, width: number | null, height: number | null): string {
  return `${isVideo ? "video" : "image"}-${getNullStringIfNeeded(formatId)}-${getNullStringIfNeeded(
    width
  )}-${getNullStringIfNeeded(height)}`
}

function getNullStringIfNeeded(value: number | undefined | null): number | string {
  if (value === undefined || value === null) {
    return "null"
  }
  return value
}

function fillRowWithMediaCreative(row: IFormMediaCreativeCampaignRow, media: IMedia): void {
  const creative = media.creatives[0]
  row.id = `${media.id}-${media.campaignMediaId}-${creative.id}`
  row.mediaId = media.id
  row.campaignMediaId = media.campaignMediaId as number
  row.creativeId = creative.id
  row.name = creative.name || "-"
  row.createdAt = creative.created_at ? new Date(creative.created_at) : null
  row.type = isImageFile(creative.mime_type) ? "image" : isVideoFile(creative.mime_type) ? "video" : "archive"
  row.approval_status_id = media.approval_status_id
  row.refusal_note = media.refusal_note
  row.isUploaded = true
}
